/* PreNumbers Critical Finance — Cap Table settings modal (reskinned).

   Shared by Critical Finance and PSBB (via contextToUse). Logic is unchanged;
   only the markup is reskinned to .pn-*. Tables use the unscoped .pn-table
   classes (not .pn-app .entity-table) so they stay styled when antd portals the
   modal to document.body. Wrapped in .pn-modal-embed for accent aliases. */

import { useState, useContext } from 'react';

import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { DeleteOutlined } from '@ant-design/icons';

import { industryDetails, capTableRenderFields, shareHoldersRenderFields, investorSplitRenderFields } from '../../constants.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { renderFbField, FbIcons } from '../../FinancialBuilder/fbUI.jsx';

const { SaveOutlined, PlusOutlined } = FbIcons;

async function saveEntityToDatabase(entity) {
  return genericPOSTDatabaseCall('/api/cfi/saveEntity', { entity }, 'entity info');
}

const handleInputBlur = (id, field, value, messageApi, callback) => {
  let newValue = value;
  if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
    messageApi.error(field.errorMessage);
    newValue = field.default || '';
  }
  if (newValue === '') {
    messageApi.error('This field cannot be empty');
    newValue = field.default || '';
  }
  callback(id, field, newValue);
};

function CapTableShareholdersTable({ entitySelected, setEntitySelected, messageApi }) {
  const [newShareholderName, setNewShareholderName] = useState('');

  const addShareholder = async () => {
    if (newShareholderName.trim() === '') {
      messageApi.error('Shareholder name cannot be empty');
      return;
    }
    messageApi.loading('Adding shareholder...', 0);
    try {
      const shareholderData = { shareholder: newShareholderName, share_percentage: 0 };
      const response = await genericPOSTDatabaseCall('/api/cfi/addShareholder', { entityId: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id, shareholder: shareholderData }, 'shareholder');
      setEntitySelected(prev => ({ ...prev, shareholders: [...prev.shareholders, response] }));
      setNewShareholderName('');
      messageApi.destroy();
      messageApi.success('Shareholder added successfully');
    } catch (error) {
      console.error('Error adding shareholder:', error);
      messageApi.destroy();
      messageApi.error('Failed to add shareholder');
    }
  };

  const deleteShareholder = async (shareholder) => {
    messageApi.loading('Delete shareholder...', 0);
    try {
      await genericDELETEDatabaseCall('/api/cfi/deleteShareholder', { shareholder }, 'shareholder');
      setEntitySelected(prev => ({ ...prev, shareholders: prev.shareholders.filter(s => s.id !== shareholder.id) }));
      messageApi.destroy();
      messageApi.success('Shareholder deleted successfully');
    } catch (error) {
      console.error('Error deleting shareholder:', error);
      messageApi.destroy();
      messageApi.error('Failed to delete shareholder');
    }
  };

  const saveShareholders = async () => {
    messageApi.loading('Saving shareholders...', 0);
    try {
      await genericPOSTDatabaseCall('/api/cfi/updateShareholders', { shareholders: entitySelected.shareholders }, 'shareholders');
      messageApi.destroy();
      messageApi.success('Shareholders saved successfully');
    } catch (error) {
      console.error('Error saving shareholders:', error);
      messageApi.destroy();
      messageApi.error('Failed to save shareholders');
    }
  };

  const handleShareholderChange = (id, field, value) => {
    const updatedShareholders = [...entitySelected.shareholders];
    const index = updatedShareholders.findIndex(shareholder => shareholder.id === id);
    if (index !== -1) {
      updatedShareholders[index] = { ...updatedShareholders[index], [field.key]: value };
    }
    setEntitySelected(prev => ({ ...prev, shareholders: updatedShareholders }));
  };

  return (
    <div className="pn-fieldset" style={{ marginTop: 22 }}>
      <div className="pn-fieldset-title">Shareholders</div>
      <div className="pn-table-wrap">
        <table className="pn-table">
          <thead>
            <tr>
              <th style={{ width: 48 }}></th>
              <th>Shareholder</th>
              <th>Share percentage</th>
            </tr>
          </thead>
          <tbody>
            {entitySelected.shareholders.map((item) => (
              <tr key={item.id}>
                <td style={{ textAlign: 'center' }}>
                  <DeleteOutlined className="delete-year-icon" onClick={() => deleteShareholder(item)} />
                </td>
                {shareHoldersRenderFields.map((field) => (
                  <td key={field.key}>
                    <div className="pn-input-wrap">
                      <input
                        className="pn-control"
                        type="text"
                        value={item[field.key]}
                        onChange={(event) => handleShareholderChange(item.id, field, event.target.value)}
                        onBlur={(event) => handleInputBlur(item.id, field, event.target.value, messageApi, handleShareholderChange)}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pn-addrow">
        <div className="pn-input-wrap" style={{ flex: 1 }}>
          <input type="text" className="pn-control" value={newShareholderName} onChange={(e) => setNewShareholderName(e.target.value)} placeholder="New shareholder name" />
        </div>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addShareholder}><PlusOutlined /> Add</button>
        <button type="button" className="pn-btn pn-btn-accent" onClick={saveShareholders}><SaveOutlined /> Save all</button>
      </div>
    </div>
  );
}

function CapTableInvestorSplitsTable({ entitySelected, setEntitySelected, messageApi }) {
  const addInvestorSplit = async () => {
    messageApi.loading('Adding investor split...', 0);
    try {
      const response = await genericPOSTDatabaseCall('/api/cfi/addInvestorSplit', { entityId: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id }, 'investor split');
      setEntitySelected(prev => ({ ...prev, investorSplits: [...prev.investorSplits, response] }));
      messageApi.destroy();
      messageApi.success('Investor split added successfully');
    } catch (error) {
      console.error('Error adding investor split:', error);
      messageApi.destroy();
      messageApi.error('Failed to add investor split');
    }
  };

  const deleteInvestorSplit = async (investorSplit) => {
    messageApi.loading('Deleting investor split...', 0);
    try {
      await genericDELETEDatabaseCall('/api/cfi/deleteInvestorSplit', { investorSplit }, 'investor split');
      setEntitySelected(prev => ({ ...prev, investorSplits: prev.investorSplits.filter(split => split.id !== investorSplit.id) }));
      messageApi.destroy();
      messageApi.success('Investor split deleted successfully');
    } catch (error) {
      console.error('Error deleting investor split:', error);
      messageApi.destroy();
      messageApi.error('Failed to delete investor split');
    }
  };

  const handleInvestorSplitChange = (id, field, value) => {
    const updatedInvestorSplits = [...entitySelected.investorSplits];
    const index = updatedInvestorSplits.findIndex(split => split.id === id);
    if (index !== -1) {
      updatedInvestorSplits[index] = { ...updatedInvestorSplits[index], [field.key]: value };
    }
    setEntitySelected(prev => ({ ...prev, investorSplits: updatedInvestorSplits }));
  };

  const saveInvestorSplits = async () => {
    messageApi.loading('Saving investor splits...', 0);
    try {
      genericPOSTDatabaseCall('/api/cfi/updateInvestorSplits', { investorSplits: entitySelected.investorSplits }, 'investor splits');
      messageApi.destroy();
      messageApi.success('Investor splits saved successfully');
    } catch (error) {
      console.error('Error saving investor splits:', error);
      messageApi.destroy();
      messageApi.error('Failed to save investor splits');
    }
  };

  return (
    <div className="pn-fieldset" style={{ marginTop: 22 }}>
      <div className="pn-fieldset-title">Cap Table Investor Splits</div>
      <div className="pn-table-wrap">
        <table className="pn-table">
          <thead>
            <tr>
              <th style={{ width: 48 }}></th>
              <th>Investor #</th>
              <th>Share split</th>
            </tr>
          </thead>
          <tbody>
            {entitySelected.investorSplits.map((split, index) => (
              <tr key={index}>
                <td style={{ textAlign: 'center' }}>
                  <DeleteOutlined className="delete-year-icon" onClick={() => deleteInvestorSplit(split)} />
                </td>
                <td>
                  <div className="pn-input-wrap">
                    <input className="pn-control" type="text" value={split.investor_name}
                      onChange={(event) => handleInvestorSplitChange(split.id, investorSplitRenderFields[0], event.target.value)}
                      onBlur={(event) => handleInputBlur(split.id, investorSplitRenderFields[0], event.target.value, messageApi, handleInvestorSplitChange)} />
                  </div>
                </td>
                <td>
                  <div className="pn-input-wrap">
                    <input className="pn-control" type="text" value={split.split_percentage}
                      onChange={(event) => handleInvestorSplitChange(split.id, investorSplitRenderFields[1], event.target.value)}
                      onBlur={(event) => handleInputBlur(split.id, investorSplitRenderFields[1], event.target.value, messageApi, handleInvestorSplitChange)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pn-addrow">
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addInvestorSplit}><PlusOutlined /> Add investor split</button>
        <button type="button" className="pn-btn pn-btn-accent" onClick={saveInvestorSplits}><SaveOutlined /> Save all</button>
      </div>
    </div>
  );
}

function CapTableSettingsModal({ contextToUse }) {
  // Use the provided context or default to CriticalFinancePageContext
  const { entitySelected, messageApi, setEntitySelected } = useContext(contextToUse || CriticalFinancePageContext);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    messageApi.loading('Saving entity info...', 0);
    try {
      await saveEntityToDatabase({ ...entitySelected, id: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id });
      messageApi.destroy();
      messageApi.success('Company info saved successfully');
    } catch (error) {
      console.error('Error saving entity info:', error);
      messageApi.destroy();
      messageApi.error('Failed to save entity info');
    }
    setSaving(false);
  };

  const handleInputChange = (event, entity, key) => {
    let newValue = event.target.value;
    setEntitySelected(prev => ({ ...prev, [key]: newValue }));
  };

  const handleInputBlurField = (event, entity, key, field) => {
    let newValue = event.target.value;
    if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
      messageApi.error(field.errorMessage);
      newValue = industryDetails[entity.industry][key] || field.default || '';
    }
    if (newValue === '') {
      messageApi.error('This field cannot be empty');
      newValue = industryDetails[entity.industry][key] || field.default || '';
    }
    setEntitySelected(prev => ({ ...prev, [key]: newValue }));
  };

  return (
    <div className="pn-modal-embed">
      <div className="pn-fieldset">
        <div className="pn-fieldset-title">Cap Table Settings</div>
        <div className="pn-fields">
          {capTableRenderFields.map(field => renderFbField(field, entitySelected, handleInputChange, true, handleInputBlurField))}
        </div>
        <div className="pn-editor-foot">
          <div className="sp" />
          <button disabled={saving} onClick={handleSave} type="button" className="pn-btn pn-btn-accent"><SaveOutlined /> Save</button>
        </div>
      </div>
      <CapTableShareholdersTable messageApi={messageApi} entitySelected={entitySelected} setEntitySelected={setEntitySelected} />
      <CapTableInvestorSplitsTable entitySelected={entitySelected} setEntitySelected={setEntitySelected} messageApi={messageApi} />
    </div>
  );
}

export default CapTableSettingsModal;
